import type { FunctionalComponent } from 'preact';
import { useState } from 'preact/hooks';
import { appIntentUrl, isAndroidBrowser, isAppReleased } from '@/lib/app-link.ts';

/**
 * '앱에서 열기' 배너. 안드로이드 브라우저(카카오톡 인앱 포함)에서만 뜬다. (2026-10-16)
 *
 * 누르면 `intent://` 로 앱을 띄워 **지금 보는 주소 그대로** 연다. 앱이 없으면 플레이스토어로
 * 가고, 설치 뒤 처음 켤 때 그 주소로 간다(`app-link.ts`).
 *
 * ★ 자동으로 앱으로 보내지 않는다. 사용자가 누를 때만 간다 — 자동 이동은 앱이 없는 사람을
 *   스토어로 끌고 가고, 뒤로가기를 누르면 다시 튕겨 나가 빠져나올 수 없게 된다.
 * ★ 닫으면 7일 동안 다시 띄우지 않는다.
 */

const DISMISS_KEY = 'app:open-banner-closed';
const DISMISS_DAYS = 7;

/** 띄우지 않을 화면 — 로그인 콜백 중이거나 적립 재생 중일 때 */
const HIDDEN_PATHS = [/^\/oauth\//, /^\/play\//];

function dismissedRecently(): boolean {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY) ?? 0);
    return Date.now() - at < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

interface Props {
  /** 지금 경로. 라우터가 바뀔 때마다 다시 그리도록 받는다. */
  path: string;
}

export const AppOpenBanner: FunctionalComponent<Props> = ({ path }) => {
  const [closed, setClosed] = useState(dismissedRecently);

  if (closed || !isAppReleased() || !isAndroidBrowser()) return null;
  if (HIDDEN_PATHS.some((pattern) => pattern.test(path))) return null;

  function close() {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // 저장이 막혀도 이번 화면에서는 닫는다.
    }
    setClosed(true);
  }

  return (
    <div class="app-open-banner" role="complementary" aria-label="앱에서 열기">
      <img class="app-open-banner-icon" src="/app-icon.svg" alt="" />
      <span class="app-open-banner-text">
        <strong>Urnplay 앱</strong>에서 더 편하게 보세요
      </span>
      {/* 주소는 누르는 순간의 것을 쓴다. 쿼리(?r=...)까지 그대로 넘어간다. */}
      <a class="app-open-banner-btn" href={appIntentUrl(`${location.pathname}${location.search}`)}>
        앱에서 열기
      </a>
      <button type="button" class="app-open-banner-close" aria-label="닫기" onClick={close}>
        ✕
      </button>
    </div>
  );
};
